<template>
  <div class="dashboard-wrapper">
    <Header />
    <DashboardStatus
      :show="showStatus"
      :type="dashboardStatusType"
      :message="dashboardStatusMessage"
      @dismiss="dismissDashboardStatus"
    ></DashboardStatus>
    <div class="dashboard-body">
      <router-view
        class="dashboard-inner-body"
        :minDate="minDate"
        :maxDate="maxDate"
        :showExplorerTimeSeriesChart="showExplorerTimeSeriesChart"
        :isComingFromInsightsCard="isComingFromInsightsCard"
        :dateRange="dateRange"
        :dateValue="dateValue"
        :startDate="startDate"
        :endDate="endDate"
        :startDateStr="startDateStr"
        :endDateStr="endDateStr"
        :isValidDateRange="isValidDateRange"
        :isValidMinDate="isValidMinDate"
        :brandIds="selectedBrandIds"
        :brandNames="brandNames"
        :brandIdsStr="brandIdsStr"
        :encodedBrandIds="encodedBrandIds"
        @loadingDashboardReport="handleIsLoadingDashboardReport"
        @showTimeSeriesUpdated="updateShowLinechart"
        @insightsExpandIconClick="updateViewFromInsights"
        @showMessage="showMessage"
        @clearSelectedFilters="clearSelectedFilters"
        @resetIsComingFromInsightsCard="isComingFromInsightsCard = false"
      ></router-view>
      <Footer v-if="isInsightsView && !isLoading" />
    </div>
  </div>
</template>

<script>
import { endOfYesterday, min, startOfDay, format, isValid } from 'date-fns';
import { unionBy } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import eventDispatcher from '../../events/dispatch';
import marketingWidgetMixin from '../../mixins/marketingWidget';
import DashboardStatus from './DashboardStatus.vue';
import Footer from './Common/Footer.vue';
import { dashboardMessages } from './Views/config/dashboardMessages';
import {
  explorerAllowedFilters,
  demographicsAllowedFilters,
  benchmarksSpecialValues,
} from  '../../services/dashboard/filtersService';
import Header from '../common/Header.vue';

export default {
  data() {
    return {
      hasError: false,
      isLoadingDashboardReport: false,
      dashboardStatus: '',
      showExplorerTimeSeriesChart: false,
      isComingFromInsightsCard: false,
    };
  },
  components: {
    DashboardStatus,
    Header,
    Footer,
  },
  mixins: [marketingWidgetMixin],
  computed: {
    ...mapState({
      activeView: state => state.main.activeView,
      isLoading: state => state.main.isLoading,
      currentDashboardReportId: state => state.dashboard.currentDashboardReportId,
      currentDemographicsReportId: state => state.dashboard.currentDemographicsReportId,
      portfolioBrands: state => state.dashboard.portfolioBrands,
      storedStartDate: state => state.dashboard.reportRequest.startDate,
      storedEndDate: state => state.dashboard.reportRequest.endDate,
      predefinedRangeValue: state => state.dashboard.reportRequest.predefinedRangeValue,
      dashboardSelectedFilters: state => state.filters.dashboardSelections.filters || [],
    }),
    ...mapGetters([
      'hasNonUSBrandSelected',
      'hasUSBrandSelected',
      'selectedBrands',
      'selectedBrandIds',
      'salesDashboardIsAvailable',
    ]),
    selectedFiltersExcludingBenchmarksSpecialFilters() {
      return this.dashboardSelectedFilters
        .filter(f => !benchmarksSpecialValues().has(f.key));
    },
    showStatus() {
      return this.hasError || this.isLoadingDashboardReport;
    },
    dashboardStatusType() {
      return this.hasError ? 'alert' : 'general';
    },
    dashboardStatusMessage() {
      return this.isLoadingDashboardReport ? 'Loading dashboard report...' : this.dashboardStatus;
    },
    isInsightsView() {
      return this.activeView === 'Insights' ||
        this.activeView === 'Sales Insights';
    },
    dateRange() {
      return {
        start: this.storedStartDate,
        end: this.storedEndDate,
      };
    },
    dateValue() {
      return {
        range: this.dateRange,
        predefinedRangeValue: this.predefinedRangeValue || 0,
      };
    },
    startDateStr() {
      return this.formatDate(this.startDate);
    },
    endDateStr() {
      return this.formatDate(this.endDate);
    },
    startDate() {
      return this.dateRange.start;
    },
    endDate() {
      return this.dateRange.end;
    },
    isValidDateRange() {
      return isValid(this.startDate)
                && isValid(this.endDate)
                && this.startDate <= this.endDate
                && this.startDate <= this.maxDate
                && this.endDate <= this.maxDate
                && this.isValidMinDate
                && this.endDate >= this.minDate;
    },
    isValidMinDate() {
      return this.startDate >= this.minDate;
    },
    brandNames() {
      return this.selectedBrands.map(b => b.key);
    },
    brandIdsStr() {
      return this.selectedBrandIds.join(', ');
    },
    encodedBrandIds() {
      return encodeURIComponent(JSON.stringify(this.selectedBrandIds));
    },
    minDate() {
      if (!this.selectedBrands || !this.selectedBrands.length) {
        return endOfYesterday();
      }

      return this.activeView === 'Sales Insights' && this.salesDashboardIsAvailable
        ? this.minSalesTrackingStartDate
        : this.minCreatedAtDate;
    },
    minCreatedAtDate() {
      const dates = this.selectedBrands.map(b => startOfDay(new Date(b.createdAt)));

      return min(dates);
    },
    minSalesTrackingStartDate() {
      const dates = this.selectedBrands.reduce((acc, b) => {
        if (b.salesTrackingStartDate) {
          acc.push(startOfDay(new Date(b.salesTrackingStartDate)));
        }

        return acc;
      }, []);

      return min(dates);
    },
    maxDate() {
      return endOfYesterday();
    },
  },
  mounted() {
    this.initializeMarketingWidget();
    window.addEventListener('restartTour', this.restartTour);
  },
  methods: {
    formatDate(date) {
      return isValid(date) ? format(date, 'yyyy-MM-dd') : 'Invalid Date';
    },
    updateShowLinechart(show) {
      this.showExplorerTimeSeriesChart = show;
    },
    clearCurrentDashboardReportIdAndPushBaseExplorerRoute() {
      if (this.currentDashboardReportId) {
        this.clearCurrentDashboardReportId();
        this.$router.push({ name: 'explorer-base' });
      }
    },
    clearCurrentDashboardReportId() {
      this.$store.commit('updateCurrentDashboardReportId', null);
    },
    updateViewFromInsights({ view, data, title }) {
      this.updateDashboardView(view, data, title);
    },
    updateDashboardView(view, data, location) {
      // If a user switches between views during onboarding, close the tour and report it as a skip
      for (const prop in this.$tours) {
        const tour = this.$tours[prop];

        if (tour.isRunning) {
          tour.skip();
        }
      }

      if (data) {
        const { metrics, dimensions, filters: cardFilters } = data;

        if (cardFilters) this.mergeCardAndDashboardFilters(cardFilters);

        if (view === 'Explorer') {
          this.$store.commit('updateReportMetrics', metrics);
          this.$store.commit('updateReportDimensions', dimensions);

          this.$store.commit('updateExplorerAllowedFilters', explorerAllowedFilters());

          if (this.currentDashboardReportId) {
            this.clearCurrentDashboardReportId();
          }
        }

        if (view === 'Shopper Intelligence') {
          this.$store.commit('updateDemographicsMetrics', metrics);
          this.$store.commit('updateDemographicsDimension', dimensions[0]);

          this.$store.commit('updateDemographicsAllowedFilters', demographicsAllowedFilters());

          this.$store.commit('updateDemographicsFilterType', 'Basic');
          this.$store.commit('updateCurrentDemographicsReportId', null);
        }
      }

      this.showExplorerTimeSeriesChart = view === 'Explorer'
        && data?.dimensions.includes('eventDate');

      this.isComingFromInsightsCard = true;

      eventDispatcher('dashboardViewChanged', {
        brand_ids: this.selectedBrandIds.join(', '),
        brand_names: this.brandNames.join(', '),
        dashboard_view: view,
        source: location,
      });

      const routeObject = this.getRouteObject(view);

      this.$router.push(routeObject);
    },
    mergeCardAndDashboardFilters(cardFilters) {
      const mergedFilters = unionBy(this.dashboardSelectedFilters, cardFilters, 'key');

      this.$store.commit(
        'updateDashboardSelectedFilters',
        mergedFilters,
      );
    },
    restartTour() {
      const tour = this.$tours[this.activeView];

      if (tour && !tour.isRunning) {
        tour.start();
      }
    },
    dismissDashboardStatus() {
      this.hasError = false;
    },
    showMessage({ type, duration, hint } = {}) {
      this.hasError = true;
      this.dashboardStatus = dashboardMessages[type] || dashboardMessages['default'];

      if (hint) {
        this.dashboardStatus = `${this.dashboardStatus} ${hint}`;
      }

      if (duration) {
        setTimeout(this.dismissDashboardStatus, duration);
      }
    },
    clearSelectedFilters() {
      if (!this.selectedFiltersExcludingBenchmarksSpecialFilters.length) return;

      this.$store.dispatch('setDashboardSelectedFilters', []);
      this.showMessage({ type: 'forceClearFilters', duration: 3000 });
    },
    handleIsLoadingDashboardReport(isLoading) {
      this.isLoadingDashboardReport = isLoading;

      if (isLoading) {
        this.hasError = false;
      }
    },
    getRouteObject(view) {
      switch (view) {
        case 'Explorer':
          return this.currentDashboardReportId
            ? { name: 'explorer-id', params: { dashboardReportId: this.currentDashboardReportId } }
            : { name: 'explorer-base' };
        case 'Shopper Intelligence':
          return this.currentDemographicsReportId
            ? {
              name: 'demographics-id',
              params: { demographicsReportId: this.currentDemographicsReportId },
            }
            : { name: 'demographics-base' };
        default:
          return { name: 'insights-base' };
      }
    },
    isNonEmptyArray(arr) {
      return Array.isArray(arr) && arr.length > 0;
    },
  },
};
</script>

<style scoped>
    .dashboard-wrapper {
        width: 100%;
        min-height: 100vh;
        font-family: "Work Sans";
        background-color: #f8fafc;
    }

    .dashboard-header {
        display: flex;
        flex-direction: row;
        align-items: center;
        height: 45px;
        padding: 8px 24px;
        box-shadow: 0 5px 8px 0 #edeff1;
        background-color: #fff;
        margin-bottom: 16px;
    }

    .header-title {
      color: #223448;
      display: flex;
      font-size: 24px;
      justify-content: left;
      width: 100%;
    }

    .dashboard-header > .header-inner > .nav-links > span {
        font-size: 18px;
        margin-right: 24px;
        color: #26c7c8;
        cursor: pointer;
        transition: .5s;
    }

    .dashboard-header > .header-inner > .nav-links > span:hover {
        color: hsl(168,60%,28%);
    }

    .dashboard-header > .header-inner > .nav-links > span.active {
        color: hsl(168,60%,28%);
        text-decoration: underline;
    }

    .header-inner {
        max-width: 1314px;
        height: 45px;
        width: 100%;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .nav-links {
        display: flex;
        align-items: center;
    }

    .glossary-link {
        display: flex;
        align-items: center;
    }

    .dashboard-body {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        max-width: 1460px;
        margin: 0 auto;
        gap: 24px;
    }

    .row {
        display: flex;
        flex-direction: row;
        margin-bottom: 30px;
        padding: 0 24px;
    }

    .column {
        display: flex;
        flex-direction: column;
        flex: 0.33;
    }

    .row.header {
        align-items: center;
    }

    .row.header > .title {
        display: flex;
        text-align: left;
        flex: 1;
    }

    .row.header > .select-report {
        flex: 3;
        text-align: left;
    }

    .row.header > .show-chart {
        flex: 0.1;
        cursor: pointer;
        background-color: white;
        box-shadow: 0 5px 8px 0 #edeff1;
        border-radius: 18px;
        font-size: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 12px;
        font-size: 14px;
        padding: 0 24px;
        height: 40px;
    }

    .row.header > .date-selector {
        flex: 0.3;
        justify-content: flex-end;
    }

    .dashboard-inner-body {
        padding: 0 24px;
    }
</style>
